import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchCampaignDetail,
  fetchSeedAccounts,
  fetchSeedOffers,
  toggleCampaign,
  toggleSeedAccounts,
  purgeCampaign,
  type CampaignDetail as CampaignDetailData,
  type SeedAccount,
} from '../../lib/seedService';
import { logger } from '../../lib/logger';

const statusColors: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-gray-100 text-gray-500 border-gray-200',
  purged: 'bg-red-50 text-red-600 border-red-200',
};

const statusLabels: Record<string, string> = {
  active: 'Active',
  archived: 'Désactivée',
  purged: 'Purgée',
};

function StatCard({ label, value, accent }: { label: string; value: number | string; accent: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
      <p className={`text-2xl font-bold ${accent}`}>{value}</p>
      <p className="text-[11px] text-gray-400 uppercase tracking-wide mt-1">{label}</p>
    </div>
  );
}

function AccountRow({ a, onToggle, loading, navigate }: {
  a: SeedAccount;
  onToggle: (a: SeedAccount) => void;
  loading: boolean;
  navigate: (p: string) => void;
}) {
  const suspended = a.status === 'suspended';
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-50 last:border-0">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 ${a.type === 'candidate' ? 'bg-indigo-500' : 'bg-purple-500'}`}>
        {a.firstName?.[0]}{a.lastName?.[0]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {a.firstName} {a.lastName}
          {a.companyName ? <span className="text-gray-400 font-normal"> — {a.companyName}</span> : null}
        </p>
        <p className="text-xs text-gray-400">{a.city || '—'}</p>
      </div>
      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-md ${suspended ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
        {suspended ? 'Désactivé' : 'Actif'}
      </span>
      <button
        onClick={() => navigate(`/command-board/traces/${a.userId}`)}
        className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
      >
        Traces
      </button>
      <button
        onClick={() => onToggle(a)}
        disabled={loading}
        className={`px-2 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-40 ${suspended ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100' : 'text-amber-700 bg-amber-50 hover:bg-amber-100'}`}
      >
        {suspended ? 'Réactiver' : 'Désactiver'}
      </button>
    </div>
  );
}

export default function CampaignDetail() {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const campaignName = decodeURIComponent(name || '');

  const [campaign, setCampaign] = useState<CampaignDetailData | null>(null);
  const [accounts, setAccounts] = useState<SeedAccount[]>([]);
  const [offers, setOffers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [purgeInput, setPurgeInput] = useState('');
  const [showPurge, setShowPurge] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, accs, offs] = await Promise.all([
        fetchCampaignDetail(campaignName),
        fetchSeedAccounts(campaignName),
        fetchSeedOffers(campaignName),
      ]);
      setCampaign(c);
      setAccounts(accs);
      setOffers(offs);
    } catch (err) {
      logger.error('load_campaign_detail', 'CampaignDetail', { error: String(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [campaignName]);

  const handleToggleCampaign = async () => {
    if (!campaign) return;
    const action = campaign.status === 'active' ? 'deactivate' : 'reactivate';
    if (action === 'deactivate' && !confirm(`Désactiver la campagne « ${campaign.name} » ?\n\nComptes gelés, offres fermées, vidéos retirées du feed. Réversible.`)) return;
    setActionLoading(true);
    try {
      const r = await toggleCampaign(campaign.name, action);
      alert(`${action === 'deactivate' ? 'Désactivée' : 'Réactivée'} : ${r.accounts} compte(s), ${r.offers} offre(s), ${r.videos} vidéo(s).`);
      load();
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAccount = async (a: SeedAccount) => {
    setActionLoading(true);
    try {
      await toggleSeedAccounts([a.userId], a.status === 'suspended' ? 'reactivate' : 'deactivate');
      load();
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePurge = async () => {
    if (!campaign || purgeInput !== campaign.name) return;
    setActionLoading(true);
    try {
      await purgeCampaign(campaign.name);
      alert('Campagne purgée.');
      navigate('/command-board');
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
      setActionLoading(false);
    }
  };

  if (loading && !campaign) {
    return <div className="p-8 text-center text-gray-400">Chargement de la campagne…</div>;
  }

  if (!campaign) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Campagne « {campaignName} » introuvable.</p>
        <button onClick={() => navigate('/command-board')} className="mt-4 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors">
          Retour au pilotage
        </button>
      </div>
    );
  }

  const candidates = accounts.filter((a) => a.type === 'candidate');
  const employers = accounts.filter((a) => a.type === 'employer');
  const activeOffers = offers.filter((o) => o.status === 'active').length;
  const i = campaign.interactions;

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/command-board')} className="mt-1 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors" title="Retour au pilotage">
            ←
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">{campaign.name}</h1>
              <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full border ${statusColors[campaign.status]}`}>
                {statusLabels[campaign.status]}
              </span>
            </div>
            {campaign.description && <p className="text-sm text-gray-500 mt-0.5">{campaign.description}</p>}
            <p className="text-xs text-gray-400 mt-1">Créée le {campaign.createdAt}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate(`/command-board/accounts?campaign=${encodeURIComponent(campaign.name)}`)}
            className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
          >
            + Ajouter des comptes
          </button>
          <button
            onClick={() => navigate(`/command-board/offers?campaign=${encodeURIComponent(campaign.name)}`)}
            className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
          >
            + Ajouter des offres
          </button>
          {campaign.status !== 'purged' && (
            <button
              onClick={handleToggleCampaign}
              disabled={actionLoading}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-40 ${campaign.status === 'active' ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'}`}
            >
              {campaign.status === 'active' ? 'Désactiver' : 'Réactiver'}
            </button>
          )}
          <button
            onClick={() => { setShowPurge(true); setPurgeInput(''); }}
            disabled={actionLoading}
            className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-40"
          >
            Purger
          </button>
        </div>
      </div>

      {/* Stats : population */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Candidats" value={candidates.length} accent="text-indigo-600" />
        <StatCard label="Recruteurs" value={employers.length} accent="text-purple-600" />
        <StatCard label="Annonces actives" value={`${activeOffers}/${offers.length}`} accent="text-fuchsia-600" />
        <StatCard label="Candidatures" value={i.applications} accent="text-rose-600" />
      </div>

      {/* Stats : interactions */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Interactions générées</h2>
        <div className="grid grid-cols-5 gap-3">
          <StatCard label="Vues vidéo" value={i.views} accent="text-sky-600" />
          <StatCard label="Likes" value={i.likes} accent="text-red-500" />
          <StatCard label="Partages" value={i.shares} accent="text-teal-600" />
          <StatCard label="Swipes" value={i.swipes} accent="text-orange-500" />
          <StatCard label="Favoris" value={i.favorites} accent="text-pink-500" />
        </div>
      </div>

      {/* Comptes */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Candidats ({candidates.length})</h3>
          </div>
          {candidates.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-gray-400">Aucun candidat seed</p>
          ) : candidates.map((a) => (
            <AccountRow key={a.userId} a={a} onToggle={handleToggleAccount} loading={actionLoading} navigate={navigate} />
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Recruteurs ({employers.length})</h3>
          </div>
          {employers.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-gray-400">Aucun recruteur seed</p>
          ) : employers.map((a) => (
            <AccountRow key={a.userId} a={a} onToggle={handleToggleAccount} loading={actionLoading} navigate={navigate} />
          ))}
        </div>
      </div>

      {/* Annonces */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Annonces ({offers.length})</h3>
        </div>
        {offers.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-gray-400">Aucune annonce seed</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left">
                <th className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase">Poste</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase">Ville</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase">Contrat</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase">Statut</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase">Créée le</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => (
                <tr key={o.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{o.positionName}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{o.city || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 uppercase">{o.contractType}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-md ${o.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {o.status === 'active' ? 'Active' : 'Fermée'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400">{o.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Purge modal */}
      {showPurge && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowPurge(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 bg-red-50">
              <h3 className="text-lg font-semibold text-red-700">Purger la campagne</h3>
              <p className="text-xs text-red-600 mt-1">
                Supprimera TOUS les comptes, offres et interactions de <strong>{campaign.name}</strong>. Irréversible.
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tapez <strong>{campaign.name}</strong> pour confirmer
              </label>
              <input
                type="text"
                value={purgeInput}
                onChange={(e) => setPurgeInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setShowPurge(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Annuler</button>
              <button
                onClick={handlePurge}
                disabled={purgeInput !== campaign.name || actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-40"
              >
                {actionLoading ? 'Purge en cours…' : 'Purger définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
